import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Box, IconButton, List, ListItemButton, Stack, Typography } from '@mui/material'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import { POI, deletePoi, loadPois } from 'src/lib/pois'

import FitnessCenterIcon from '@mui/icons-material/FitnessCenter'
import HomeIcon from '@mui/icons-material/Home'
import SchoolIcon from '@mui/icons-material/School'
import StarIcon from '@mui/icons-material/Star'
import WorkIcon from '@mui/icons-material/Work'

const ICON_MAP: Record<string, React.ReactNode> = {
    home: <HomeIcon sx={{ color: 'primary.main' }} />,
    work: <WorkIcon sx={{ color: 'primary.main' }} />,
    school: <SchoolIcon sx={{ color: 'primary.main' }} />,
    workout: <FitnessCenterIcon sx={{ color: 'primary.main' }} />,
    star: <StarIcon sx={{ color: 'primary.main' }} />,
}

export const Route = createFileRoute('/plan/poi/$selectType/')({
    component: SelectPOI,
})

function SelectPOI() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { selectType } = Route.useParams()

    const [pois, setPois] = useState<POI[]>([])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setPois(loadPois())
    }, [])

    const handleSelectPOI = (poi: POI) => {
        navigate({
            to: '/plan',
            search: (prev: unknown) => ({
                ...(prev as Record<string, unknown>),
                [selectType]: {
                    title: poi.name,
                    latitude: poi.lat,
                    longitude: poi.lon,
                    type: 'POI' as const,
                },
            }),
        } as Parameters<typeof navigate>[0])
    }

    const handleDelete = (e: React.MouseEvent, poiId: string) => {
        e.stopPropagation()
        deletePoi(poiId)
        setPois(loadPois())
    }

    const handleEdit = (e: React.MouseEvent, poi: POI) => {
        e.stopPropagation()
        navigate({
            to: '/plan/poi',
            state: { editingPoi: poi } as Record<string, unknown>,
        })
    }

    if (pois.length === 0) {
        return (
            <AutomaticSheet>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('No saved places yet')}
                    </Typography>
                </Box>
            </AutomaticSheet>
        )
    }

    return (
        <AutomaticSheet>
            <List>
                {pois.map(poi => (
                    <ListItemButton
                        key={poi.id}
                        onClick={() => handleSelectPOI(poi)}
                        sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack direction="row" alignItems="center" sx={{ width: '100%', py: 0.5 }}>
                            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                                {ICON_MAP[poi.icon] ?? <StarIcon sx={{ color: 'primary.main' }} />}
                            </Box>
                            <Typography variant="body1" flex={1} noWrap>{poi.name}</Typography>
                            <IconButton size="small" onClick={e => handleEdit(e, poi)} sx={{ color: 'text.secondary' }}>
                                <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton size="small" onClick={e => handleDelete(e, poi.id)} sx={{ color: 'error.main' }}>
                                <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <NavigateNextIcon sx={{ color: 'primary.main', ml: 0.5 }} />
                        </Stack>
                    </ListItemButton>
                ))}
            </List>
        </AutomaticSheet>
    )
}

export default SelectPOI
